/**
 * OSM (On-Site Marketing) Client
 * Shopify storefront에서 동작하는 캠페인 rule validator + event dispatcher.
 */
import { buildOsmAmplitudeProps, isAmplitudeHostAllowed } from './amplitude-tracker.js';

(() => {
  // ---------------------------------------------------------------------------
  // Internal state
  // ---------------------------------------------------------------------------

  let _config = null;
  let _campaigns = [];
  let _debug = false;
  let _campaignsReady = false;
  let _fetchSeq = 0;
  /** @type {{ version: number, bundle: Record<string, unknown> } | null} */
  let _widgetChrome = null;

  const log = (...args) => {
    if (_debug) console.log('[OSM]', ...args);
  };

  // =========================================================================
  // 1. Cart State Manager
  // =========================================================================

  const CartState = (() => {
    let _cart = null;
    let _prevCart = null;
    const _listeners = [];

    const notify = (eventType, requestBody) => {
      for (const fn of _listeners) {
        try { fn(_cart, _prevCart, eventType, requestBody); }
        catch (e) { console.error('[OSM] CartState listener error:', e); }
      }
    };

    const parseRequestBody = (_input, init) => {
      if (!init?.body) return null;
      try {
        if (typeof init.body === 'string') return JSON.parse(init.body);
        if (init.body instanceof FormData) {
          const obj = {};
          init.body.forEach((v, k) => { obj[k] = v; });
          return obj;
        }
      } catch { /* ignore */ }
      return null;
    };

    const getCartEndpointType = (url) => {
      const path = typeof url === 'string' ? url : (url?.url ?? '');
      if (path.includes('/cart/add')) return 'add';
      if (path.includes('/cart/change')) return 'change';
      if (path.includes('/cart/update')) return 'update';
      if (path.includes('/cart/clear')) return 'clear';
      return null;
    };

    // Monkey-patch fetch
    const _originalFetch = window.fetch.bind(window);
    window.fetch = async (input, init) => {
      const endpointType = getCartEndpointType(input);
      const reqBody = endpointType ? parseRequestBody(input, init) : null;
      const response = await _originalFetch(input, init);

      if (endpointType) {
        const cloned = response.clone();
        cloned.json().then(async (data) => {
          if (endpointType === 'add') {
            // /cart/add.js returns the line item, not the full cart
            const cart = await _originalFetch('/cart.js').then(r => r.json());
            _prevCart = _cart;
            _cart = cart;
            log('cart updated (add)', _cart);
            notify('add', reqBody);
          } else {
            _prevCart = _cart;
            _cart = data;
            log(`cart updated (${endpointType})`, _cart);
            notify(endpointType, reqBody);
          }
        }).catch(() => { /* non-json response */ });
      }

      return response;
    };

    // Patch XMLHttpRequest for jQuery.ajax themes
    const _origXhrOpen = XMLHttpRequest.prototype.open;
    const _origXhrSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function(method, url, ...rest) {
      this._osmEndpointType = getCartEndpointType(url);
      return _origXhrOpen.call(this, method, url, ...rest);
    };

    XMLHttpRequest.prototype.send = function(body) {
      const endpointType = this._osmEndpointType;
      let reqBody = null;
      if (endpointType && body) {
        try { reqBody = JSON.parse(body); } catch { /* ignore */ }
      }

      if (endpointType) {
        this.addEventListener('load', () => {
          try {
            if (endpointType === 'add') {
              _originalFetch('/cart.js').then(r => r.json()).then(cart => {
                _prevCart = _cart;
                _cart = cart;
                notify('add', reqBody);
              });
            } else {
              const data = JSON.parse(this.responseText);
              _prevCart = _cart;
              _cart = data;
              notify(endpointType, reqBody);
            }
          } catch { /* ignore */ }
        });
      }
      return _origXhrSend.call(this, body);
    };

    return {
      get current() { return _cart; },
      get previous() { return _prevCart; },

      async load() {
        _cart = await _originalFetch('/cart.js').then(r => r.json());
        log('cart loaded', _cart);
        return _cart;
      },

      onChange(fn) {
        _listeners.push(fn);
      },
    };
  })();

  // =========================================================================
  // 2. Customer Condition Validators
  // =========================================================================

  const getCustomerId = () => {
    if (window.__st?.cid) return window.__st.cid;
    return document.querySelector('meta[name="shopify-customer-id"]')?.content ?? null;
  };

  const _PAGE_VIEW_MAP_KEY = 'osm_page_view_map';

  const getPageViewMap = () => {
    try { return JSON.parse(sessionStorage.getItem(_PAGE_VIEW_MAP_KEY) || '{}'); }
    catch { return {}; }
  };

  const savePageViewMap = (map) => {
    sessionStorage.setItem(_PAGE_VIEW_MAP_KEY, JSON.stringify(map));
  };

  // Returns the view count for the current path
  const getPageViewCount = () => {
    const map = getPageViewMap();
    return map[window.location.pathname] ?? 0;
  };

  // Increments the view count for the current path and returns the new count
  const incrementPageViewCount = () => {
    const map = getPageViewMap();
    const path = window.location.pathname;
    map[path] = (map[path] ?? 0) + 1;
    savePageViewMap(map);
    return map[path];
  };

  // True when no paths have been recorded yet — i.e. this is the first visit in the session
  const isFirstVisit = () => Object.keys(getPageViewMap()).length === 0;

  const getLandingPageUrl = () => {
    const key = 'osm_landing_page_url';
    let stored = sessionStorage.getItem(key);
    if (!stored) {
      stored = window.location.href;
      sessionStorage.setItem(key, stored);
    }
    return stored;
  };

  const isMobileDevice = () => {
    if (/Mobi|Android|iPhone|iPad|iPod|webOS|BlackBerry|Opera Mini|IEMobile/i.test(navigator.userAgent)) return true;
    return window.innerWidth <= 768;
  };

  const isShopifyApp = () =>
    /Shopify(Mobile)?/i.test(navigator.userAgent);

  const customerValidators = {
    // --- cart ---
    'cart.is_empty'(rule) {
      const cart = CartState.current;
      if (!cart) return rule.value === true;
      const empty = cart.item_count === 0;
      return rule.value === true ? empty : !empty;
    },

    'cart.contains'(rule) {
      const { items } = CartState.current ?? {};
      if (!items) return false;
      const val = String(rule.value).toLowerCase();
      return items.some(item =>
        String(item.variant_id) === val ||
        String(item.product_id) === val ||
        item.handle === val ||
        item.product_title?.toLowerCase().includes(val)
      );
    },

    'cart.does_not_contain'(rule) {
      return !customerValidators['cart.contains'](rule);
    },

    'cart.total_greater_than'(rule) {
      const cart = CartState.current;
      return cart ? cart.total_price > Number(rule.value) * 100 : false;
    },

    'cart.total_less_than'(rule) {
      const cart = CartState.current;
      return cart ? cart.total_price < Number(rule.value) * 100 : false;
    },

    // --- customer ---
    'customer.is_logged_in'(rule) {
      const cid = getCustomerId();
      return rule.value === true ? !!cid : !cid;
    },

    'customer.is_not_logged_in'(rule) {
      const cid = getCustomerId();
      return rule.value === true ? !cid : !!cid;
    },

    // --- page_view_count ---
    'page_view_count.greater_than'(rule) {
      return getPageViewCount() > Number(rule.value);
    },

    'page_view_count.less_than'(rule) {
      return getPageViewCount() < Number(rule.value);
    },

    // --- traffic_source ---
    'traffic_source.utm_contains'(rule) {
      const val = String(rule.value).toLowerCase();
      const params = new URLSearchParams(window.location.search);
      for (const [k, v] of params) {
        if (k.startsWith('utm_') && v.toLowerCase().includes(val)) return true;
      }
      return false;
    },

    'traffic_source.referrer_url_contains'(rule) {
      return (document.referrer || '').toLowerCase().includes(String(rule.value).toLowerCase());
    },

    'traffic_source.landing_page_url'(rule) {
      return getLandingPageUrl().toLowerCase().includes(String(rule.value).toLowerCase());
    },

    // --- device ---
    'device.is_mobile'(rule) {
      return rule.value === true ? isMobileDevice() : !isMobileDevice();
    },

    'device.is_desktop'(rule) {
      const desktop = !isMobileDevice();
      return rule.value === true ? desktop : !desktop;
    },

    'device.is_app'(rule) {
      return rule.value === true ? isShopifyApp() : !isShopifyApp();
    },
  };

  // =========================================================================
  // 3. Trigger Condition Validators
  // =========================================================================

  const matchPath = (operator, ruleValue, actualPath) => {
    const val = String(ruleValue);
    const path = String(actualPath);
    switch (operator) {
      case 'equals': return path === val;
      case 'not_equals': return path !== val;
      case 'contains': return path.includes(val);
      case 'starts_with': return path.startsWith(val);
      case 'ends_with': return path.endsWith(val);
      case 'matches_pattern':
        try { return new RegExp(val).test(path); } catch { return false; }
      default: return path === val;
    }
  };

  const matchCartItem = (items, field, value) => {
    if (!items?.length) return false;

    switch (field) {
      case 'equal_to': {
        const target = typeof value === 'object' ? value : {};
        const variantId = target.variant_id ?? target.id;
        const productId = target.product_id;
        return items.some(item => {
          if (variantId && String(item.variant_id ?? item.id) === String(variantId)) return true;
          if (productId && String(item.product_id) === String(productId)) return true;
          return false;
        });
      }
      case 'quantity': {
        const totalQty = items.reduce((sum, item) => sum + (item.quantity || 1), 0);
        return totalQty >= Number(value);
      }
      case 'is_in_collection':
        // TODO: stub — needs server-side resolution or /products/{handle}.js fetch
        log('is_in_collection: stub, returning false');
        return false;
      default:
        return false;
    }
  };

  const triggerValidators = {
    // --- page_view ---
    'page_view.path'(rule, eventData) {
      const path = eventData?.path ?? window.location.pathname;
      const operator = rule.op ?? rule.operator ?? 'equals';
      return matchPath(operator, rule.value, path);
    },

    // --- mouse_enter ---
    'mouse_enter.target_id'(rule, eventData) {
      return eventData?.targetId === String(rule.value);
    },

    'mouse_enter.target_class'(rule, eventData) {
      return eventData?.targetClasses?.includes(String(rule.value)) ?? false;
    },

    // --- mouse_leave (delegates to mouse_enter) ---
    'mouse_leave.target_id'(rule, eventData) {
      return triggerValidators['mouse_enter.target_id'](rule, eventData);
    },

    'mouse_leave.target_class'(rule, eventData) {
      return triggerValidators['mouse_enter.target_class'](rule, eventData);
    },

    // --- click ---
    'click.target_id'(rule, eventData) {
      return eventData?.targetId === String(rule.value);
    },

    'click.target_class'(rule, eventData) {
      return eventData?.targetClasses?.includes(String(rule.value)) ?? false;
    },

    'click.anything'() {
      return true;
    },

    // --- add_to_cart ---
    'add_to_cart.equal_to'(rule, eventData) {
      return matchCartItem(eventData?.items ?? [], 'equal_to', rule.value);
    },

    'add_to_cart.quantity'(rule, eventData) {
      return matchCartItem(eventData?.items ?? [], 'quantity', rule.value);
    },

    'add_to_cart.is_in_collection'(rule, eventData) {
      return matchCartItem(eventData?.items ?? [], 'is_in_collection', rule.value);
    },

    // --- remove_from_cart ---
    'remove_from_cart.equal_to'(rule, eventData) {
      return matchCartItem(eventData?.items ?? [], 'equal_to', rule.value);
    },

    'remove_from_cart.quantity'(rule, eventData) {
      return matchCartItem(eventData?.items ?? [], 'quantity', rule.value);
    },

    'remove_from_cart.is_in_collection'(rule, eventData) {
      return matchCartItem(eventData?.items ?? [], 'is_in_collection', rule.value);
    },
  };

  // =========================================================================
  // 4. Condition Evaluator
  // =========================================================================

  const evaluateRule = (rule, validatorMap, eventData) => {
    const key = `${rule.category}.${rule.field}`;
    const fn = validatorMap[key];
    if (!fn) {
      log(`no validator for ${key} — defaulting to false`);
      return false;
    }
    const result = fn(rule, eventData);
    log('rule', key, '=', result, rule);
    return result;
  };

  const evaluateConditionGroup = (group, validatorMap, eventData) => {
    if (!group?.rules?.length) return true;

    const op = group.op ?? group.operator ?? 'and';
    const { rules } = group;

    for (const item of rules) {
      const result = item.rules
        ? evaluateConditionGroup(item, validatorMap, eventData)
        : evaluateRule(item, validatorMap, eventData);

      if (op === 'and' && !result) return false;
      if (op === 'or' && result) return true;
    }

    return op === 'and';
  };

  // =========================================================================
  // 5. Fatigue Manager
  // =========================================================================

  const FatigueManager = {
    _key: (campaignId, suffix) => `osm_${campaignId}_${suffix}`,

    check(campaignId, fatigue) {
      if (!fatigue) return true;
      const now = Date.now();

      if (fatigue.cooldown_hours) {
        const lastShown = parseInt(localStorage.getItem(this._key(campaignId, 'last_shown')) || '0', 10);
        if (lastShown && (now - lastShown) < fatigue.cooldown_hours * 3_600_000) {
          log('fatigue: cooldown active for campaign', campaignId);
          return false;
        }
      }

      if (fatigue.frequency_cap) {
        const count = parseInt(localStorage.getItem(this._key(campaignId, 'shown_count')) || '0', 10);
        if (count >= fatigue.frequency_cap) {
          log('fatigue: frequency cap reached for campaign', campaignId);
          return false;
        }
      }

      return true;
    },

    record(campaignId) {
      const countKey = this._key(campaignId, 'shown_count');
      const count = parseInt(localStorage.getItem(countKey) || '0', 10);
      localStorage.setItem(countKey, String(count + 1));
      localStorage.setItem(this._key(campaignId, 'last_shown'), String(Date.now()));
    },
  };

  // =========================================================================
  // 6. Trigger Dispatcher
  // =========================================================================

  const onTriggerFired = (triggerEvent, eventData) => {
    if (!_campaignsReady) {
      log('trigger suppressed — campaigns not ready yet:', triggerEvent);
      return;
    }
    log('trigger fired:', triggerEvent, eventData);

    let match = null;

    log("_campaigns", _campaigns)

    for (const entry of _campaigns) {
      log('entry.campaign.trigger_event', entry.campaign.trigger_event)
      log('triggerEvent', triggerEvent)
      log('fatigue', FatigueManager.check(entry.campaign.id, entry.campaign.fatigue))
      if (entry.campaign.trigger_event !== triggerEvent) continue;
      log('trigger condition pass')
      if (!FatigueManager.check(entry.campaign.id, entry.campaign.fatigue)) continue;

      log('fatigue check pass')
      for (const group of entry.groups) {
        const triggerPass = evaluateConditionGroup(
          group.conditions.trigger, triggerValidators, eventData
        );
        if (!triggerPass) {
          log('group', group.id, 'trigger conditions failed');
          continue;
        }

        match = { campaign: entry.campaign, group };
        break;
      }

      log("MATCH ::::::::::: ", match)

      if (match) break;
    }

    if (!match) {
      log('no matching group for trigger', triggerEvent);
      return;
    }

    log('matched group', match.group.id, 'in campaign', match.campaign.id);
    const delayMs = match.group.widget?.delay_ms ?? 0;
    const doRender = () => {
      renderWidget(match.campaign, match.group);
      FatigueManager.record(match.campaign.id);
    };
    if (delayMs > 0) {
      log('delaying render by', delayMs, 'ms');
      setTimeout(doRender, delayMs);
    } else {
      doRender();
    }
  };

  // -- Event listener registration --

  // Always registered — page navigations require a refetch because page_view_count
  // and (on first visit) traffic_source conditions are re-evaluated server-side.
  const registerPageViewListeners = () => {
    const handleNavigation = async () => {
      incrementPageViewCount();
      await refreshCampaigns();
      onTriggerFired('page_view', { path: window.location.pathname });
    };

    window.addEventListener('popstate', handleNavigation);

    const origPush = history.pushState.bind(history);
    history.pushState = (...args) => {
      origPush(...args);
      handleNavigation();
    };
  };

  const registerMouseEventListeners = () => {
    if (!_campaigns.some(c => c.campaign.trigger_event === 'mouse_event')) return;

    const buildEventData = (e) => ({
      targetId: e.target.id || '',
      targetClasses: e.target.className ? String(e.target.className).split(/\s+/) : [],
    });

    document.addEventListener('mouseenter', (e) => {
      onTriggerFired('mouse_event', { ...buildEventData(e), subType: 'mouse_enter' });
    }, true);

    document.addEventListener('mouseleave', (e) => {
      onTriggerFired('mouse_event', { ...buildEventData(e), subType: 'mouse_leave' });
    }, true);

    document.addEventListener('click', (e) => {
      onTriggerFired('mouse_event', { ...buildEventData(e), subType: 'click' });
    });
  };

  // Always registered — cart changes must trigger a refetch regardless of whether
  // any campaign uses 'cart_event', because customer_conditions (cart.*) are
  // re-evaluated server-side on every fetch.
  const registerCartRefetchListener = () => {
    CartState.onChange(async (cart, prevCart, endpointType, requestBody) => {
      if (!endpointType) return;

      // Capture diff before refreshing, so we can dispatch the right cart trigger
      // after the new campaign list is ready.
      let pendingTrigger = null;

      if (endpointType === 'add' && requestBody) {
        const items = requestBody.items ?? (
          requestBody.id
            ? [{ id: requestBody.id, variant_id: requestBody.id, quantity: requestBody.quantity || 1 }]
            : []
        );
        pendingTrigger = { subType: 'add_to_cart', items, cart };
      }

      if (endpointType === 'change' || endpointType === 'update') {
        if (prevCart?.items && cart?.items) {
          const currentQtyMap = new Map(cart.items.map(i => [i.variant_id, i.quantity]));
          const removedItems = prevCart.items
            .filter(i => (currentQtyMap.get(i.variant_id) ?? 0) < i.quantity)
            .map(i => ({
              variant_id: i.variant_id,
              product_id: i.product_id,
              quantity: i.quantity - (currentQtyMap.get(i.variant_id) ?? 0),
            }));

          const prevQtyMap = new Map(prevCart.items.map(i => [i.variant_id, i.quantity]));
          const addedItems = cart.items
            .filter(i => i.quantity > (prevQtyMap.get(i.variant_id) ?? 0))
            .map(i => ({
              variant_id: i.variant_id,
              product_id: i.product_id,
              quantity: i.quantity - (prevQtyMap.get(i.variant_id) ?? 0),
            }));

          // Prefer reporting the removal; both can't realistically happen together
          if (removedItems.length) {
            pendingTrigger = { subType: 'remove_from_cart', items: removedItems, cart };
          } else if (addedItems.length) {
            pendingTrigger = { subType: 'add_to_cart', items: addedItems, cart };
          }
        }
      }

      await refreshCampaigns();

      if (pendingTrigger) {
        onTriggerFired('cart_event', pendingTrigger);
      }
    });
  };

  // =========================================================================
  // 7. Widget Renderer
  // =========================================================================

  const escapeHtml = (str) =>
    String(str ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

  /**
   * When HTML was over-escaped for JSON/storage (&lt;p&gt;…), assigning to innerHTML shows tags as text.
   * Decode only when it looks entity-encoded so normal `<p>…` from TipTap is unchanged.
   */
  const normalizeRichTextForInnerHtml = (raw) => {
    if (raw == null) return '';
    const s0 = typeof raw === 'string' ? raw : String(raw);
    const s = s0.trim();
    if (!s) return '';
    const looksEntityEncoded =
      (s.includes('&lt;') && s.includes('&gt;')) || (s.includes('&#60;') && s.includes('&#62;'));
    if (!looksEntityEncoded) return s;
    const ta = document.createElement('textarea');
    let cur = s;
    for (let i = 0; i < 4; i++) {
      ta.innerHTML = cur;
      const next = ta.value;
      if (next === cur) break;
      cur = next;
    }
    return cur;
  };

  /** Strip tags for alt text and plain previews (trusted admin HTML in widget title/body). */
  const htmlToPlainText = (html) => {
    if (!html) return '';
    const d = document.createElement('div');
    d.innerHTML = normalizeRichTextForInnerHtml(html);
    return (d.textContent || '').trim();
  };

  /**
   * 클립보드에 텍스트를 복사하고 성공 여부를 반환
   * @param {string} text
   */
  const copyToClipboard = async (text) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      const success = document.execCommand('copy');
      document.body.removeChild(textArea);
      return success;
    } catch (err) {
      console.error('[OSM] clipboard copy failed:', err);
      return false;
    }
  };

  /**
   * 쿠폰 코드 복사 성공 시 피드백 토스트 표시
   * @param {string} couponCode
   */
  const showCouponCopiedFeedback = (couponCode) => {
    const existing = document.getElementById('osm-coupon-feedback');
    if (existing) existing.remove();

    const feedback = document.createElement('div');
    feedback.id = 'osm-coupon-feedback';
    feedback.innerHTML = `
      <div style="${getCouponFeedbackInnerBoxStyle()}">
        ✓ 쿠폰 코드가 복사되었습니다: <strong>${escapeHtml(couponCode)}</strong>
      </div>
    `;

    document.body.appendChild(feedback);

    setTimeout(() => {
      feedback.firstElementChild.style.animation = 'osm-fade-out 0.3s ease';
      setTimeout(() => feedback.remove(), 300);
    }, 3000);
  };

  const OSM_STYLES_FALLBACK = `
    #osm-widget-container {
      position: absolute;
      width: 100%;
      height: 100%;
    }
    #osm-banner-inner {
      transition: opacity 0.3s ease, transform 0.3s ease;
      opacity: 0;
      transform: translateY(8px);
      align-items: flex-start;
      padding: 16px 16px;
      gap: 24px;
      width: 500px;
      height: 100%;
      max-height: 200px;

      @media screen and (max-width: 1024px) {
        width: 500px;
      }

      @media screen and (max-width: 768px) {
        gap: 12px;
        padding: 0.5rem 0.5rem;
        width: 400px;
        max-height: 160px;
      }

      @media screen and (max-width: 480px) {
        width: 95%;
      }
    }
    #osm-banner-close {
      position: relative;
      flex-shrink: 0;
      align-self: flex-start;
      width: 32px;
      height: 32px;

      @media screen and (max-width: 768px) {
        width: 24px;
        height: 24px;
      }
    }
    #osm-banner-inner.osm-visible {
      opacity: 1;
      transform: translateY(0);
    }
    #osm-banner-img-cont, #osm-toast-img-cont {
      height: 100%;
      width: 100%;
      max-width: 30%;
      flex-shrink: 0;
      display: flex;
      align-items: center;
    }
    #osm-banner-img-cont > img {
      width: 100%;
      height: auto;
      aspect-ratio: 1/1;
      object-fit: cover;
      border-radius: 6px;
    }
    #osm-banner-text-cont, #osm-toast-text-cont {
      display: flex;
      flex-direction: column;
      gap: 8px;
      flex: 1;
      min-width: 0;
      min-height: 0;
      color: white;
    }
    #osm-banner-text-cont .osm-banner-title-html,
    #osm-banner-text-cont .osm-banner-title-html p,
    #osm-toast-text-cont .osm-banner-title-html,
    #osm-toast-text-cont .osm-banner-title-html p {
      font-weight: 600;
      font-size: 1.25rem;
      margin: 0;

      @media screen and (max-width: 768px) {
        font-size: 1rem;
      }
    }
    #osm-banner-text-cont .osm-banner-body-html,
    #osm-banner-text-cont .osm-banner-body-html p,
    #osm-toast-text-cont .osm-banner-body-html,
    #osm-toast-text-cont .osm-banner-body-html p {
      font-size: 1rem;
      margin: 0;

      @media screen and (max-width: 768px) {
        font-size: 0.875rem;
      }
    }
    @keyframes osm-draw-circle {
      0%   { stroke-dashoffset: 88; }
      100% { stroke-dashoffset: 0; }
    }
    .osm-timer-circle {
      animation: osm-draw-circle 20s linear forwards;
    }
    @keyframes osm-fade-in-up {
      from {
        opacity: 0;
        transform: translate(-50%, 10px);
      }
      to {
        opacity: 1;
        transform: translate(-50%, 0);
      }
    }
    @keyframes osm-fade-out {
      from { opacity: 1; }
      to { opacity: 0; }
    }
    #osm-toast-anchor {
      position: fixed;
      z-index: 1000;
    }
    #osm-toast-inner {
      transition: opacity 0.3s ease, transform 0.3s ease;
      opacity: 0;
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      box-sizing: border-box;
      background: rgba(22, 23, 24, 0.4);
      border-radius: 24px;
      box-shadow: 0px 0px 5px 2px rgba(22, 23, 24, 0.5);
      backdrop-filter: blur(25px);
      min-width: 320px;
      padding: 1rem;
      gap: 24px;
      width: 500px;
      height: 160px;

      @media screen and (max-width: 1024px) {
        width: 500px;
      }

      @media screen and (max-width: 768px) {
        gap: 12px;
        padding: 0.75rem;
        width: 400px;
        height: 140px;
      }

      @media screen and (max-width: 480px) {
        width: 95%;
        padding: 0.5rem;
        height: 100%;
        max-height: 120px;
      }
    }

    #osm-toast-img-cont > img {
      height: auto;
      aspect-ratio: 1/1;
      border-radius: 24px;
    }

    #osm-toast-inner.osm-visible {
      opacity: 1;
    }
    #osm-toast-inner.osm-slide-in-right {
      transform: translateX(100%);
    }
    #osm-toast-inner.osm-slide-in-right.osm-visible {
      transform: translateX(0);
    }
    #osm-toast-inner.osm-slide-in-left {
      transform: translateX(-100%);
    }
    #osm-toast-inner.osm-slide-in-left.osm-visible {
      transform: translateX(0);
    }
    #osm-toast-inner.osm-slide-in-bottom {
      transform: translateY(100%);
    }
    #osm-toast-inner.osm-slide-in-bottom.osm-visible {
      transform: translateY(0);
    }

    /* No padding (banner & toast) — inner has class osm-no-padding when widget.no_padding */
    #osm-banner-inner.osm-no-padding {
      padding: 0;
      height: auto;
      max-height: none;
    }
    @media screen and (max-width: 768px) {
      #osm-banner-inner.osm-no-padding {
        padding: 0;
        height: auto;
        max-height: none;
      }
    }
    @media screen and (max-width: 480px) {
      #osm-banner-inner.osm-no-padding {
        padding: 0;
        height: auto;
        max-height: none;
      }
    }
    #osm-banner-inner.osm-no-padding #osm-banner-img-cont > img {
      border-radius: 0;
      border-top-left-radius: 24px;
      border-bottom-left-radius: 24px;
    }
    #osm-banner-inner.osm-no-padding #osm-banner-text-cont,
    #osm-toast-inner.osm-no-padding #osm-toast-text-cont {
      padding-block: 0.5rem;
    }
    #osm-banner-inner.osm-no-padding #osm-banner-close {
      margin: 0.5rem;
    }

    #osm-toast-inner.osm-no-padding {
      padding: 0;
      height: auto;
      max-height: none;
    }
    @media screen and (max-width: 768px) {
      #osm-toast-inner.osm-no-padding {
        padding: 0;
        height: auto;
        max-height: none;
      }
    }
    @media screen and (max-width: 480px) {
      #osm-toast-inner.osm-no-padding {
        padding: 0;
        height: auto;
        max-height: none;
      }
    }
    #osm-toast-inner.osm-no-padding #osm-toast-img-cont > img {
      border-radius: 0;
      border-top-left-radius: 24px;
      border-bottom-left-radius: 24px;
    }
  `;

  function getActiveGlobalCss() {
    const raw = _widgetChrome?.bundle?.global_css;
    return typeof raw === 'string' && raw.trim() ? raw : OSM_STYLES_FALLBACK;
  }

  function injectStyles() {
    const text = getActiveGlobalCss();
    let el = document.getElementById('osm-styles');
    if (!el) {
      el = document.createElement('style');
      el.id = 'osm-styles';
      document.head.appendChild(el);
    }
    el.textContent = text;
  }

  /**
   * @param {unknown} chrome top-level `widget_chrome` from osm-active-campaigns JSON
   */
  function applyWidgetChrome(chrome) {
    if (!chrome || typeof chrome !== 'object') return;
    const c = /** @type {{ version?: unknown, bundle?: unknown }} */ (chrome);
    if (!c.bundle || typeof c.bundle !== 'object') return;
    _widgetChrome = {
      version: typeof c.version === 'number' && Number.isFinite(c.version) ? c.version : 0,
      bundle: /** @type {Record<string, unknown>} */ (c.bundle),
    };
    injectStyles();
  }

  /** Fixed anchor offsets — shared by banner inner and toast anchor (see Pony `BANNER_POSITION_STYLE`). */
  const OSM_FLOAT_WIDGET_POSITION_FALLBACK = {
    bottom_left: 'bottom: 5%; left: 5%; transform: none;',
    bottom_center: 'bottom: 5%; left: 50%; transform: translateX(-50%);',
    bottom_right: 'bottom: 5%; right: 5%; transform: none;',
    top_left: 'top: 5%; left: 5%; transform: none;',
    top_center: 'top: 5%; left: 50%; transform: translateX(-50%);',
    top_right: 'top: 5%; right: 5%; transform: none;',
    center_left: 'top: 50%; left: 5%; transform: translateY(-50%);',
    center: 'top: 50%; left: 50%; transform: translate(-50%, -50%);',
    center_right: 'top: 50%; right: 5%; transform: translateY(-50%);',
    cart_icon: 'bottom: 5%; right: 5%; transform: none;',
  };

  const BANNER_INNER_INLINE_SUFFIX_FALLBACK =
    'display: flex; flex-direction: row; background: rgba(22, 23, 24, 0.4); border-radius: 24px; z-index: 1000; min-width: 320px; box-shadow: 0px 0px 5px 2px rgba(22, 23, 24, 0.5); backdrop-filter: blur(25px);';

  const BANNER_CLOSE_INLINE_FALLBACK =
    'display: flex; align-items: center; justify-content: center; color: white; border-radius: 50%; cursor: pointer; background: transparent; border: none; padding: 0;';

  const COUPON_FEEDBACK_BOX_STYLE_FALLBACK =
    'position: fixed; bottom: 20px; left: 50%; transform: translateX(-50%); background: rgba(22, 23, 24, 0.9); color: white; padding: 12px 24px; border-radius: 8px; font-size: 14px; z-index: 10001; box-shadow: 0 4px 12px rgba(0,0,0,0.3); backdrop-filter: blur(10px); animation: osm-fade-in-up 0.3s ease;';

  function getFloatPositionStyles() {
    const raw = _widgetChrome?.bundle?.float_position_css;
    const out = { ...OSM_FLOAT_WIDGET_POSITION_FALLBACK };
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      for (const [k, v] of Object.entries(/** @type {Record<string, unknown>} */ (raw))) {
        if (typeof v === 'string' && v.trim()) out[k] = v.trim();
      }
    }
    return out;
  }

  function getBannerInnerInlineSuffix() {
    const sh = _widgetChrome?.bundle?.banner_shell;
    if (!sh || typeof sh !== 'object') return BANNER_INNER_INLINE_SUFFIX_FALLBACK;
    const s = /** @type {{ inner_inline_suffix?: unknown }} */ (sh).inner_inline_suffix;
    return typeof s === 'string' && s.trim() ? s.trim() : BANNER_INNER_INLINE_SUFFIX_FALLBACK;
  }

  function getBannerCloseInlineStyle() {
    const sh = _widgetChrome?.bundle?.banner_shell;
    if (!sh || typeof sh !== 'object') return BANNER_CLOSE_INLINE_FALLBACK;
    const s = /** @type {{ close_button_inline_style?: unknown }} */ (sh).close_button_inline_style;
    return typeof s === 'string' && s.trim() ? s.trim() : BANNER_CLOSE_INLINE_FALLBACK;
  }

  function getCouponFeedbackInnerBoxStyle() {
    const cf = _widgetChrome?.bundle?.coupon_feedback;
    if (!cf || typeof cf !== 'object') return COUPON_FEEDBACK_BOX_STYLE_FALLBACK;
    const s = /** @type {{ inner_box_style?: unknown }} */ (cf).inner_box_style;
    return typeof s === 'string' && s.trim() ? s.trim() : COUPON_FEEDBACK_BOX_STYLE_FALLBACK;
  }

  /** @param {Record<string, unknown>} widget @param {Record<string, unknown> | null} promotion */
  const resolveWidgetImageUrl = (widget, promotion) => {
    const mode = widget.image_url_mode;
    const custom = widget.image_url;
    const resolved = promotion?.resolved_image_url;
    const legacy = promotion?.image_url;
    if (mode === 'custom') return (custom && String(custom)) || '';
    if (mode === 'content') return (resolved && String(resolved)) || (legacy && String(legacy)) || '';
    return (custom && String(custom)) || (resolved && String(resolved)) || (legacy && String(legacy)) || '';
  };

  /** @param {Record<string, unknown>} widget @param {Record<string, unknown> | null} promotion */
  const resolveWidgetToUrl = (widget, promotion) => {
    const baseUrlRaw = _config?.baseUrl != null ? String(_config.baseUrl) : '';
    const BASE_URL = baseUrlRaw.replace(/\/+$/, '');

    const mode = widget.to_url_mode;
    const custom = widget.to_url;

    if (mode === 'custom') {
      return (custom && String(custom)) || null;
    }

    if (mode === 'content') {
      const dest = promotion?.destination_url;
      if (dest) {
        const destStr = String(dest);
        if (destStr.startsWith('/')) {
          return BASE_URL ? `${BASE_URL}${destStr}` : destStr;
        }
        return destStr;
      }

      const ph = widget.product_handle && String(widget.product_handle).trim();
      if (ph) {
        return BASE_URL ? `${BASE_URL}/products/${ph}` : `/products/${ph}`;
      }
      const ch = widget.collection_handle && String(widget.collection_handle).trim();
      if (ch) {
        return BASE_URL ? `${BASE_URL}/collections/${ch}` : `/collections/${ch}`;
      }
      if (widget.page_handle) {
        const path = String(widget.page_handle);
        const fullPath = path.startsWith('/') ? path : `/${path}`;
        return BASE_URL ? `${BASE_URL}${fullPath}` : fullPath;
      }

      return null;
    }

    if (custom) return String(custom);
    const dest = promotion?.destination_url;
    if (dest) {
      const destStr = String(dest);
      if (destStr.startsWith('/')) {
        return BASE_URL ? `${BASE_URL}${destStr}` : destStr;
      }
      return destStr;
    }
    return null;
  };

  const renderBanner = (campaign, group) => {
    const viewport = isMobileDevice() ? 'mobile' : 'desktop';
    const { widget, promotion } = group;
    const position = widget.position[viewport];

    const floatMap = getFloatPositionStyles();
    const posStyle = floatMap[position] ?? floatMap.bottom_center;

    const promotionContentType = promotion?.content_type ?? promotion?.type ?? 'none';
    const imageUrl = resolveWidgetImageUrl(widget, promotion);
    const hasImage = !!imageUrl;
    const titlePlain = htmlToPlainText(widget.title);
    const imgAlt = escapeHtml(titlePlain || 'Banner');

    const container = document.createElement('div');
    container.id = 'osm-widget-container';
    container.dataset.osmCampaign = campaign.id;
    container.dataset.osmGroup = group.id;
    container.dataset.osmTemplate = 'banner';
    container.dataset.osmPosition = position;

    container.innerHTML = `
      <div
        id="osm-banner-inner"
        style="position: fixed; ${posStyle} ${getBannerInnerInlineSuffix()}"
      >
        ${hasImage ? `
          <div id="osm-banner-img-cont">
            <img src="${escapeHtml(imageUrl)}" alt="${imgAlt}" width="auto" height="auto">
          </div>
        ` : ''}

        <div id="osm-banner-text-cont"></div>

        <button
          id="osm-banner-close"
          type="button"
          style="${getBannerCloseInlineStyle()}"
        >
          <svg style="position:absolute;inset:0;width:100%;height:100%;transform:rotate(-90deg);" viewBox="0 0 32 32">
            <circle cx="16" cy="16" r="14" stroke="rgba(255,255,255,0.2)" stroke-width="2" fill="none"/>
            <circle cx="16" cy="16" r="14" stroke="white" stroke-width="2" fill="none"
              stroke-linecap="round" class="osm-timer-circle"
              stroke-dasharray="88" stroke-dashoffset="88"/>
          </svg>
          <svg xmlns="http://www.w3.org/2000/svg" style="width:16px;height:16px;z-index:1;" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>
    `;

    const inner = container.querySelector('#osm-banner-inner');
    if (inner && widget.no_padding) inner.classList.add('osm-no-padding');
    if (inner) {
      inner.dataset.ampEvent = 'OSM Interactions';
      inner.dataset.ampOsmCampaign = String(campaign.id);
      inner.dataset.ampOsmCampaignGroup = String(group.id);
      inner.dataset.ampOsmContentType = String(promotionContentType);
      inner.dataset.ampOsmTemplateType = String(widget.template ?? 'banner');
      inner.dataset.ampOsmTriggerEvent = String(campaign.trigger_event ?? '');
    }
    const textCont = container.querySelector('#osm-banner-text-cont');
    if (textCont) {
      if (widget.title) {
        const titleEl = document.createElement('div');
        titleEl.className = 'osm-banner-title-html';
        titleEl.innerHTML = normalizeRichTextForInnerHtml(widget.title);
        textCont.appendChild(titleEl);
      }
      if (widget.body) {
        const bodyEl = document.createElement('div');
        bodyEl.className = 'osm-banner-body-html';
        bodyEl.innerHTML = normalizeRichTextForInnerHtml(widget.body);
        textCont.appendChild(bodyEl);
      }
    }

    const handleBannerClick = (e) => {
      if (e.target.closest('#osm-banner-close')) return;

      if (!promotion) {
        const toUrl = resolveWidgetToUrl(widget, null);
        if (toUrl) {
          window.location.href = toUrl;
        }
        return;
      }

      const { content_type, content_value } = promotion;

      if (content_type === 'coupon_code' && content_value) {
        copyToClipboard(String(content_value)).then((success) => {
          if (success) {
            log('coupon code copied:', content_value);
            showCouponCopiedFeedback(String(content_value));
          } else {
            log('coupon code copy failed');
            alert(`쿠폰 코드: ${content_value}`);
          }
        });
      } else {
        const toUrl = resolveWidgetToUrl(widget, promotion);
        if (toUrl) {
          window.location.href = toUrl;
        }
      }
    };

    const hasAction = promotion || resolveWidgetToUrl(widget, null);
    if (hasAction) {
      inner.style.cursor = 'pointer';
      inner.addEventListener('click', handleBannerClick);
    }

    // Close button — dismiss immediately, let animation play out
    container.querySelector('#osm-banner-close').addEventListener('click', (e) => {
      e.stopPropagation();
      inner.classList.remove('osm-visible');
      inner.addEventListener('transitionend', () => container.remove(), { once: true });
    });

    return container;
  };

  const renderToast = (campaign, group) => {
    const viewport = isMobileDevice() ? 'mobile' : 'desktop';
    const { widget, promotion } = group;
    const position = widget.position[viewport];

    const floatMapToast = getFloatPositionStyles();
    const posStyle = floatMapToast[position] ?? floatMapToast.bottom_right;

    const getSlideClass = (pos) => {
      if (String(pos).includes('left')) return 'osm-slide-in-left';
      if (String(pos).includes('right')) return 'osm-slide-in-right';
      if (String(pos).includes('bottom')) return 'osm-slide-in-bottom';
      return 'osm-slide-in-right';
    };
    const slideClass = getSlideClass(position);

    const imageUrl = resolveWidgetImageUrl(widget, promotion);
    const hasImage = !!imageUrl;
    const titlePlain = htmlToPlainText(widget.title);
    const imgAlt = escapeHtml(titlePlain || 'Toast');

    const container = document.createElement('div');
    container.id = 'osm-widget-container';
    container.dataset.osmCampaign = campaign.id;
    container.dataset.osmGroup = group.id;
    container.dataset.osmTemplate = 'toast';
    container.dataset.osmPosition = position;

    container.innerHTML = `
      <div id="osm-toast-anchor" style="${posStyle}">
        <div
          id="osm-toast-inner"
          class="${slideClass}"
        >
          ${hasImage ? `
            <div id="osm-toast-img-cont">
              <img src="${escapeHtml(imageUrl)}" alt="${imgAlt}" width="auto" height="auto">
            </div>
          ` : ''}

          <div id="osm-toast-text-cont"></div>
        </div>
      </div>
    `;

    const inner = container.querySelector('#osm-toast-inner');
    if (inner && widget.no_padding) inner.classList.add('osm-no-padding');
    const textCont = container.querySelector('#osm-toast-text-cont');

    if (textCont) {
      if (widget.title) {
        const titleEl = document.createElement('div');
        titleEl.className = 'osm-banner-title-html';
        titleEl.innerHTML = normalizeRichTextForInnerHtml(widget.title);
        textCont.appendChild(titleEl);
      }
      if (widget.body) {
        const bodyEl = document.createElement('div');
        bodyEl.className = 'osm-banner-body-html';
        bodyEl.innerHTML = normalizeRichTextForInnerHtml(widget.body);
        textCont.appendChild(bodyEl);
      }
    }

    let toastDismissed = false;
    const dismissToast = (innerEl, containerEl) => {
      if (toastDismissed || !containerEl.isConnected) return;
      toastDismissed = true;
      innerEl.classList.remove('osm-visible');
      innerEl.addEventListener('transitionend', () => containerEl.remove(), { once: true });
    };

    const handleToastClick = () => {
      if (!promotion) {
        const toUrl = resolveWidgetToUrl(widget, null);
        if (toUrl) window.location.href = toUrl;
        return;
      }

      const { content_type, content_value } = promotion;

      if (content_type === 'coupon_code' && content_value) {
        copyToClipboard(String(content_value)).then((success) => {
          if (success) {
            log('coupon code copied:', content_value);
            showCouponCopiedFeedback(String(content_value));
            dismissToast(inner, container);
          } else {
            log('coupon code copy failed');
            alert(`쿠폰 코드: ${content_value}`);
          }
        });
      } else {
        const toUrl = resolveWidgetToUrl(widget, promotion);
        if (toUrl) window.location.href = toUrl;
      }
    };

    const hasAction = promotion || resolveWidgetToUrl(widget, null);
    if (hasAction) {
      inner.style.cursor = 'pointer';
      inner.addEventListener('click', handleToastClick);
    }

    const dismissRaw = widget.toast_dismiss_ms;
    let autoDismissMs = 5000;
    if (typeof dismissRaw === 'number' && Number.isFinite(dismissRaw)) {
      autoDismissMs = Math.max(1000, Math.min(120000, Math.floor(dismissRaw)));
    }
    setTimeout(() => {
      if (document.body.contains(container)) {
        dismissToast(inner, container);
      }
    }, autoDismissMs);

    const promotionContentTypeToast = promotion?.content_type ?? promotion?.type ?? 'none';
    if (inner) {
      inner.dataset.ampEvent = 'OSM Interactions';
      inner.dataset.ampOsmCampaign = String(campaign.id);
      inner.dataset.ampOsmCampaignGroup = String(group.id);
      inner.dataset.ampOsmContentType = String(promotionContentTypeToast);
      inner.dataset.ampOsmTemplateType = String(widget.template ?? 'toast');
      inner.dataset.ampOsmTriggerEvent = String(campaign.trigger_event ?? '');
    }

    return container;
  };

  const renderWidget = (campaign, group) => {
    const { widget } = group;
    const viewport = isMobileDevice() ? 'mobile' : 'desktop';

    log('renderWidget', {
      campaignId: campaign.id,
      groupId: group.id,
      template: widget.template,
      position: widget.position[viewport],
    });

    injectStyles();
    document.getElementById('osm-widget-container')?.remove();

    let container;
    if (widget.template === 'toast') {
      container = renderToast(campaign, group);
    } else {
      container = renderBanner(campaign, group);
    }

    document.body.appendChild(container);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = container.querySelector('#osm-banner-inner, #osm-toast-inner');
        el?.classList.add('osm-visible');
        const { promotion } = group;
        const contentType = promotion?.content_type ?? promotion?.type ?? 'none';
        const props = buildOsmAmplitudeProps({
          campaignId: campaign.id,
          groupId: group.id,
          contentType,
          templateType: widget.template ?? 'banner',
          triggerEvent: campaign.trigger_event ?? '',
        });
        if (isAmplitudeHostAllowed()) {
          window.amplitude?.track('OSM Triggered', props);
        }
      });
    });
  };

  // =========================================================================
  // 8. Init — Public API
  // =========================================================================

  const fetchActiveCampaigns = async ({ endpoint }) => {
    const customerId = getCustomerId();
    const cart = CartState.current;

    const body = {};
    if (customerId != null) body.customer_id = customerId;
    if (cart != null) {
      body.cart = {
        token: cart.token,
        item_count: cart.item_count,
        total_price: cart.total_price,
        items: (cart.items ?? []).map((i) => ({
          variant_id: i.variant_id,
          product_id: i.product_id,
          quantity: i.quantity,
          handle: i.handle,
          product_title: i.product_title,
        })),
      };
    }
    // page_view_count = current path's visit count in this session
    body.page_view_count = getPageViewCount();
    // traffic_source is only meaningful on first visit; omit on subsequent refetches
    // so the server evaluates utm/referrer/landing_page conditions only at initial entry
    if (isFirstVisit()) {
      body.traffic_source = {
        query_string: window.location.search,
        referrer: document.referrer || '',
        landing_page_url: getLandingPageUrl(),
      };
    }
    body.device = {
      is_mobile: isMobileDevice(),
      is_desktop: !isMobileDevice(),
      is_app: isShopifyApp(),
    };

    log('fetchActiveCampaigns body:', body);

    const res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`OSM fetch failed: ${res.status}`);

    const data = await res.json();
    applyWidgetChrome(data.widget_chrome);
    const rows = Array.isArray(data) ? data : (data.campaigns ?? []);
    if (!rows.length) return [];

    // Legacy edge shape: { campaign, groups }[]
    const first = rows[0];
    if (first?.campaign && Array.isArray(first.groups)) return rows;

    // Flat shape (osm-active-campaigns): one row per campaign with id, fatigue, conditions, widget, promotion
    return rows.map((row) => ({
      campaign: {
        id: row.id,
        trigger_event: row.trigger_event,
        fatigue: row.fatigue ?? {},
      },
      groups: [
        {
          id: row.id,
          conditions: row.conditions ?? { trigger: { operator: 'and', rules: [] } },
          widget: row.widget,
          promotion: row.promotion ?? null,
        },
      ],
    }));
  };

  // Fetches eligible campaigns from the server with the latest client context.
  // Sets _campaignsReady=false while fetching so no trigger fires stale widget.
  // Uses a sequence number to discard responses from superseded requests.
  const refreshCampaigns = async () => {
    _campaignsReady = false;
    const mySeq = ++_fetchSeq;
    try {
      const campaigns = await fetchActiveCampaigns(_config);
      if (mySeq !== _fetchSeq) {
        log('refreshCampaigns: stale response discarded (seq mismatch)');
        return;
      }
      _campaigns = campaigns;
      log('refreshCampaigns: campaigns updated', _campaigns.length);
    } catch (e) {
      log('refreshCampaigns failed:', e.message);
    } finally {
      if (mySeq === _fetchSeq) _campaignsReady = true;
    }
  };

  window.OsmClient = {
    /**
     * @param {{ endpoint: string, baseUrl?: string, debug?: boolean }} config
     */
    async init(config) {
      _config = config;
      _debug = !!config.debug;
      log('initializing', config);

      // Capture landing page URL before anything else changes the session state
      getLandingPageUrl();

      try {
        await CartState.load().catch(e => {
          log('cart load failed (guest/empty):', e.message);
        });

        // Register listeners first. Triggers are suppressed by _campaignsReady=false
        // until the first fetch completes.
        registerPageViewListeners();
        registerMouseEventListeners();
        registerCartRefetchListener();
        log('all listeners registered');

        // First fetch: counts the current page visit, then fires initial page_view trigger
        incrementPageViewCount();
        await refreshCampaigns();
        onTriggerFired('page_view', { path: window.location.pathname });
      } catch (e) {
        console.error('[OSM] init failed:', e);
      }
    },

    _internal: {
      CartState,
      customerValidators,
      triggerValidators,
      evaluateConditionGroup,
      FatigueManager,
      onTriggerFired,
      refreshCampaigns,
    },
  };
})();